/**
 * 스킬바.
 *
 * 스킬은 배우러 갈 곳이 없다 — 레벨이 되면 그냥 생긴다(game/skills.ts).
 * 그러니 **생겼다는 사실을 화면이 알려줘야 한다.** 안 그러면 있는 줄도 모른다.
 *
 * ── 스탯 창이 여기 있었다 ────────────────────────────────────────
 * 우상단에 스탯 배분 창을 따로 띄웠는데, **가방에 같은 것이 이미 있었다.**
 * 표를 두 곳에 그리면 한쪽이 반드시 낡는다 — 실제로 검사 항목을 셋에서 여섯으로 늘릴 때
 * 두 곳이 따로 낡았다. 그리고 안 그래도 좁은 화면을 상시로 먹었다.
 *
 * 없애도 되는 조건은 이미 갖춰져 있었다 —
 * 포인트가 생기면 **가방 칸에 배지**가 뜨고(Q276 — 늘 보이는 다섯 칸 `#mainbar [data-m="inv"].badge`, main-bar.js),
 * 가방을 열면 스탯 구역이 상설로 있다. 알림과 배분이 각각 제자리에 있는 셈이다.
 * 관리자 부여 버튼만 갈 곳이 없어서 그 스탯 구역으로 옮겼다.
 */
import { S } from '../core/state.js';
import { send } from '../core/net.js';
import { SFX } from '../core/sound.js';
import { observeUiHeight } from './readability.js';

let host = null;
let measureHeight = () => {};

/** 글자 대신 즉시 알아보는 코드 내장 아이콘. 색은 CSS currentColor라 쿨다운도 함께 흐려진다. */
const SKILL_ICON = {
  strike: '<svg viewBox="0 0 24 24"><path d="M5 19 18 6M13 5l6-1-1 6M4 20l5-1-4-4Z"/></svg>',
  sweep: '<svg viewBox="0 0 24 24"><path d="M4 16c4 5 13 5 17-2M5 15l-2 1 1 3M8 12l9-7 2 2-8 8"/></svg>',
  pierce: '<svg viewBox="0 0 24 24"><path d="M3 12h17M15 7l6 5-6 5M6 8l-3 4 3 4"/></svg>',
  poison: '<svg viewBox="0 0 24 24"><path d="M12 3c4 5 6 8 6 12a6 6 0 0 1-12 0c0-4 2-7 6-12Z"/><circle cx="10" cy="15" r="1.5"/><circle cx="14" cy="12" r="1"/></svg>',
  frenzy: '<svg viewBox="0 0 24 24"><path d="M13 2c2 5-2 6 1 9 2-2 3-3 3-5 4 4 4 9 1 13a8 8 0 0 1-12-10c1 3 3 4 4 5-1-5 1-8 3-12Z"/></svg>',
  execute: '<svg viewBox="0 0 24 24"><path d="m5 5 14 14M19 5 5 19M4 3l4 1-3 4M20 3l-4 1 3 4"/></svg>',
  guard: '<svg viewBox="0 0 24 24"><path d="M12 2 20 5v6c0 5-3 9-8 11-5-2-8-6-8-11V5Z"/><path d="M8 12h8M12 8v8"/></svg>',
  volley: '<svg viewBox="0 0 24 24"><path d="M3 6h15M14 3l5 3-5 3M3 12h18M17 9l5 3-5 3M3 18h15M14 15l5 3-5 3"/></svg>',
  blast: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="5"/><path d="M12 1v5M12 18v5M1 12h5M18 12h5M4 4l4 4M16 16l4 4M20 4l-4 4M8 16l-4 4"/></svg>',
  rend: '<svg viewBox="0 0 24 24"><path d="M12 2 20 5v6c0 5-3 9-8 11-5-2-8-6-8-11V5Z"/><path d="M7 4l10 14M15 3 9 20"/></svg>',
  snare: '<svg viewBox="0 0 24 24"><circle cx="12" cy="14" r="6"/><path d="M12 8V2M9 3l3-1 3 1M8 19l-2 3M16 19l2 3"/></svg>',
  vanish: '<svg viewBox="0 0 24 24"><path d="M3 12s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6Z"/><path d="m4 4 16 16"/></svg>',
  ambush: '<svg viewBox="0 0 24 24"><path d="M4 20 20 4M7 4l13 0-3 3M4 17v3h3"/><path d="m14 14 6 6"/></svg>',
  cheapshot: '<svg viewBox="0 0 24 24"><path d="m4 20 7-7M13 11l7-7M8 4l12 12"/><circle cx="8" cy="4" r="2"/></svg>',
};

function ensure() {
  if (host) return;
  host = document.createElement('div');
  host.id = 'skills';
  document.getElementById('game').appendChild(host);
  measureHeight = observeUiHeight(host, '--skillh');
}

/**
 * ★ **쿨다운 숫자 때문에 스킬 버튼을 초당 10번 갈아치우고 있었다.**
 *
 * 서명에 `q.cd` 가 들어 있어서, 하나라도 식는 중이면 매 틱 `innerHTML` 을 통째로
 * 다시 썼다. 그러면 사용자가 누르고 떼는 사이에 버튼 엘리먼트가 교체되고
 * 브라우저는 click 을 아예 발생시키지 않는다 — **식는 중인 1번 때문에 준비된
 * 2번이 안 먹힌다.** 상점 버튼에서 이미 겪고 `panels/index.js` 에 재렌더 가드를
 * 만들어둔 바로 그 사고인데, 스킬바만 그 교훈 밖에 있었다.
 * (키보드 1~5는 멀쩡했으므로 **손가락으로 누르는 사람만** 겪는다 — 모바일)
 *
 * 그래서 **구조**(어떤 스킬이 있나)와 **상태**(얼마나 남았나)를 나눈다.
 * 구조가 바뀔 때만 다시 만들고, 쿨다운은 있는 엘리먼트의 글자만 고친다.
 */
let shape = null;
export const usableSkills = y => (y?.skills || []).filter(q => !q.passive);

/**
 * R212(레드팀 D2-18) — 스킬 한 번 누름(버튼·숫자 키 공통). 판정은 서버가 하므로 요청은 그대로 보낸다(화면의 쿨다운 숫자는
 * 최대 한 틱 낡았다 — 막 식은 스킬을 화면이 먼저 막으면 손이 늦는다). **시전음은 쓸 수 있을 때만** 낸다: 0.15초 넘게 식는
 * 중이거나 연결이 끊긴 순간에는 시전음 대신 짧은 거절음과 버튼 흔들림. 전에는 식는 중에도 「슉」 소리만 나고 아무 일이
 * 없어 「씹혔다」로 느꼈다.
 */
const COOL_SLACK_SECS = 0.15;
export function castSkill(i) {
  const q = usableSkills(S.you)[i];
  if (!q) return;
  const blocked = !S.connected || Number(q.cd) > COOL_SLACK_SECS;
  send({ t: 'skill', id: q.id });
  if (!blocked) { SFX.playerSkill(S.you?.klass); return; }
  SFX.deny();
  const b = host?.querySelectorAll('button')[i];
  if (b) { b.classList.remove('deny'); void b.offsetWidth; b.classList.add('deny'); setTimeout(() => b.classList.remove('deny'), 320); }
}
export function syncSkills() {
  const y = S.you; if (!y) return;
  ensure();
  const sk = usableSkills(y);
  // Empty bars measure zero; learned skills reserve their actual text/row height.
  const s = sk.map((q) => `${q.id}:${q.rank}:${q.auto ? 1 : 0}`).join(',');
  if (s !== shape) {
    shape = s;
    host.innerHTML = sk.map((q, i) =>
      `<button type="button" class="sk${q.auto ? ' auto' : ''}" data-i="${i}" aria-label="${q.name}${q.auto ? ' · 자동 사용 중' : ''}" title="${q.name} — ${q.desc}${q.rank ? ` · +${q.rank} 효과 +${q.boost}%` : ''}${q.auto ? ' · 자동 사용 ON' : ''}">
        <b class="sk-key">${i + 1}</b><span class="sk-icon">${SKILL_ICON[q.id] || ''}</span>
        <span class="sk-name">${q.name}</span>${q.rank ? `<small class="sk-rank">+${q.rank}</small>` : ''}${q.auto ? '<small class="sk-auto">자동</small>' : ''}<i></i></button>`).join('');
    host.style.display = sk.length ? 'flex' : 'none';
    measureHeight();
    for (const b of host.querySelectorAll('button')) b.onclick = () => castSkill(Number(b.dataset.i));
  }
  // 상태만 갱신 — 엘리먼트는 그대로 두므로 누르는 중에 사라지지 않는다
  const btns = host.querySelectorAll('button');
  forgetSkillCooldowns((id) => !sk.some((q) => q.id === id && q.cd > 0));
  sk.forEach((q, i) => {
    const b = btns[i]; if (!b) return;
    const ready = q.cd <= 0;
    b.classList.toggle('cd', !ready);
    const t = ready ? '' : q.cd < 10 ? q.cd.toFixed(1) : String(Math.ceil(q.cd));
    const el = b.querySelector('i');
    if (el && el.textContent !== t) el.textContent = t;
    // 묶음 HUD1 — 원형으로 줄어드는 덮개(남은 몫 = 남은 초 ÷ 이번 재사용 시간)
    const p = ready ? '' : cdShare(q.id, q.cd).toFixed(1);
    if (b.style.getPropertyValue('--cdp') !== p) { if (p) b.style.setProperty('--cdp', p); else b.style.removeProperty('--cdp'); }
  });
}

/**
 * 묶음 HUD1(DESIGN 14.468) — 재사용 대기는 **원형으로 줄어드는 덮개 + 숫자**(참고 게임의 시계 덮개). 서버는 남은 초(`cd`)만 보낸다 —
 * 전체 길이는 민첩·패시브·환급에 따라 달라서(서버 `skillCoolMult`·`celerityCoolMultOf`) 표를 화면에 베끼면 갈라진다. 그래서 식기
 * 시작한 첫 값(쓴 직후의 남은 초)을 이번 재사용 시간으로 기억한다. 0 이 되면 잊는다. 식는 중에 접속해 첫 값을 못 봤으면 그때 값을 전체로 본다.
 */
const cdFull = new Map();
function cdShare(id, cd) {
  const was = cdFull.get(id);
  const full = was && cd <= was + 0.05 ? was : cd;   // 더 길어졌으면(다시 씀·새로 받음) 새 값이 전체
  cdFull.set(id, full);
  return full > 0 ? Math.max(0, Math.min(100, cd / full * 100)) : 0;
}
export function forgetSkillCooldowns(ready) { for (const id of [...cdFull.keys()]) if (ready(id)) cdFull.delete(id); }
